import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { GradientButton } from '../ui/GradientButton';
import { getCustomGreeting, updateCustomGreeting, generateGreetingAudio } from '../../api';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import AlertMessage from '../ui/AlertMessage';

const CustomGreetingSection = () => {
  const { t } = useTranslation();
  const [greeting, setGreeting] = useState('');
  const [language, setLanguage] = useState('en');
  const [audioUrl, setAudioUrl] = useState('');
  const [audioBlob, setAudioBlob] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchCustomGreeting();
  }, []);

  const fetchAudioFile = async (url) => {
    try {
      const token = localStorage.getItem('jwtToken');
      // Add base URL to the relative path
      const fullUrl = `${process.env.REACT_APP_SOCKET_IO_URL}${url}`;
      
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include', // Add this line
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch audio');
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setAudioBlob(blobUrl);
    } catch (error) {
      console.error('Error fetching audio file:', error);
      setAlert({ type: 'error', message: t('CustomGreetingSection.audio_fetch_error') });
    }
  };
  

  // Update useEffect to fetch audio when audioUrl changes
  useEffect(() => {
    if (audioUrl) {
      fetchAudioFile(audioUrl);
    }
    // Cleanup function to revoke blob URL
    return () => {
      if (audioBlob) {
        URL.revokeObjectURL(audioBlob);
      }
    };
  }, [audioUrl]);

  const fetchCustomGreeting = async () => {
    try {
      setLoading(true);
      const response = await getCustomGreeting();
      setGreeting(response.greeting || '');
      setLanguage(response.language || 'en');
      setAudioUrl(response.audioUrl || '');
    } catch (err) {
      console.error('Fetch Custom Greeting Error:', err);
      setAlert({ type: 'error', message: t('CustomGreetingSection.fetch_error') });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAudio = async () => {
    try {
      const response = await generateGreetingAudio();
      setAudioUrl(response.audioUrl);
      setAlert({ type: 'success', message: t('CustomGreetingSection.save_success') });
    } catch (err) {
      console.error('Generate Audio Error:', err);
      setAlert({ type: 'error', message: t('CustomGreetingSection.audio_generate_error') });
    }
  };
  
  const handleSave = async () => {
    try {
      setLoading(true);
      await updateCustomGreeting({ greeting, language });
      setAlert({ type: 'success', message: t('CustomGreetingSection.save_success') });
      setShowModal(false);

      // Trigger audio generation immediately after saving
      await handleGenerateAudio();
    } catch (err) {
      console.error('Save Custom Greeting Error:', err);
      setAlert({ type: 'error', message: t('CustomGreetingSection.save_error') });
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value;
    setLanguage(selectedLanguage);
  };

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div>
        <h2 className="text-2xl font-semibold mb-2">
          {t('CustomGreetingSection.title')}
        </h2>
        <p>{t('CustomGreetingSection.description')}</p>
      </div>

      {/* Alert Message */}
      {alert.message && (
        <AlertMessage type={alert.type} message={alert.message} />
      )}

      {/* Greeting Display */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">
            {t('CustomGreetingSection.current_greeting')}
          </h3>
          <GradientButton onClick={() => setShowModal(true)}>
            {t('CustomGreetingSection.edit_button')}
          </GradientButton>
        </div>
        <p className="text-gray-700">{greeting || t('CustomGreetingSection.no_greeting')}</p>
        {/* Updated Audio Player */}
        {audioBlob && (
          <div className="mt-4">
            <audio 
              ref={audioRef}
              controls 
              key={audioBlob}
              onError={(e) => console.error('Audio player error:', e)}
            >
              <source src={audioBlob} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
            <button
              onClick={() => window.open(audioBlob, '_blank')}
              className="text-blue-500 underline ml-2"
            >
              {t('CustomGreetingSection.listen_link')}
            </button>
          </div>
        )}
      </div>

      {/* Modal for Editing Greeting - Unchanged */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={t('CustomGreetingSection.modal.title')}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('CustomGreetingSection.modal.greeting_label')}
            </label>
            <textarea
              className="w-full rounded-lg border-gray-300 focus:ring-violet-500 focus:border-violet-500"
              rows="4"
              value={greeting}
              onChange={(e) => setGreeting(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('CustomGreetingSection.modal.language_label')}
            </label>
            <select
              className="w-full rounded-lg border-gray-300 focus:ring-violet-500 focus:border-violet-500"
              value={language}
              onChange={handleLanguageChange}
            >
              <option value="en">{t('CustomGreetingSection.languages.en')}</option>
              <option value="fr">{t('CustomGreetingSection.languages.fr')}</option>
              <option value="de">{t('CustomGreetingSection.languages.de')}</option>
            </select>
          </div>
          <div className="flex justify-end space-x-4">
            <GradientButton
              type="button"
              onClick={() => setShowModal(false)}
              className="bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              {t('CustomGreetingSection.cancel_button')}
            </GradientButton>
            <GradientButton onClick={handleSave} disabled={loading}>
              {loading ? t('CustomGreetingSection.saving') : t('CustomGreetingSection.save_button')}
            </GradientButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CustomGreetingSection;
